import * as ProgressPrimitive from "@radix-ui/react-progress";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Pasek postępu.
 *
 * Primityw powstał, bo ten sam pasek był sklejony z divów w TRZECH miejscach
 * (pobieranie w odtwarzaczu, wysyłanie pliku, postęp odcinka na kafelku), a rolę
 * `progressbar` i wartości dla czytnika ekranu nosiło tylko jedno z nich. Przez
 * Radix, bo to on pilnuje `aria-valuemin`/`max`/`now` i stanu nieokreślonego.
 *
 * **Stan nieokreślony to `value === null`**, nie zero. Pobieranie z hosta, który
 * nie podaje długości treści, nie ma procentu — pasek na zerze kłamałby, że nic
 * się nie dzieje, a pasek pulsujący mówi „trwa, nie wiem ile zostało".
 */
export function Progress({
  className,
  value,
  ...props
}: Omit<React.ComponentProps<typeof ProgressPrimitive.Root>, "value"> & {
  value: number | null;
}) {
  const indeterminate = value === null;
  // Poza zakresem 0–100 pasek wyglądałby na zepsuty, a wartości bierzemy
  // z serwera — jedno dzielenie przez zero po drodze i mamy NaN.
  const clamped = indeterminate || !Number.isFinite(value) ? 0 : Math.min(100, Math.max(0, value));

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      value={indeterminate ? null : clamped}
      className={cn("relative h-2 w-full overflow-hidden rounded-full bg-secondary", className)}
      {...props}
    >
      {indeterminate ? (
        <div
          data-slot="progress-indeterminate"
          // Ruch po szynie rysuje `styles.css`; pulsowanie w miejscu wyglądało
          // jak pasek zatrzymany na jednej trzeciej.
          className="h-full w-1/3 rounded-full bg-gradient-to-r from-primary to-accent"
        />
      ) : (
        <ProgressPrimitive.Indicator
          data-slot="progress-indicator"
          className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-[width] duration-500 ease-tv"
          style={{ width: `${clamped}%` }}
        />
      )}
    </ProgressPrimitive.Root>
  );
}
